package org.vreo.devicehub.features.core.data.device.networking

import io.ktor.client.HttpClient
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.ParametersBuilder
import io.ktor.http.URLProtocol
import io.ktor.http.contentType
import io.ktor.http.path

import org.vreo.devicehub.features.core.data.safeCall
import org.vreo.devicehub.features.core.data.safeCallEmpty
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.data.device.dto.AnprDeviceRequestDto
import org.vreo.devicehub.features.core.data.device.dto.AnprDetailsResponseDto
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceDataSource

/**
 * Remote data source for devices. Uses Ktor client to make requests to server and access device data.
 *
 * @property httpClient Ktor client.
 * @property hostUrl Host url.
 * @property getDevicesRoute route for accessing list of available devices
 * @property addDeviceToAccountRoute route for linking/adding device to user account
 * @property updateDeviceDetailsRoute route for updating device's details, such as TAG
 * @property removeDeviceFromAccountRoute route for unlinking/remove device from user account
 * @property registerProductionDeviceRoute route for registering new production device
 * @property deleteDeviceFromProductionRoute route for deleting device from operation and all
 * services
 * @property urlProtocol Url protocol.
 */
class KtorRemoteDeviceDataSource(
    private val httpClient: HttpClient,
    private val hostUrl: String,
    private val getDevicesRoute: String,
    private val addDeviceToAccountRoute: String,
    private val updateDeviceDetailsRoute: String,
    private val removeDeviceFromAccountRoute: String,
    private val registerProductionDeviceRoute: String,
    private val deleteDeviceFromProductionRoute: String,
    private val productionDevicesFileRoute: String,
    private val urlProtocol: URLProtocol = URLProtocol.HTTPS
) : RemoteDeviceDataSource {
    /**
     * Get user's devices (devices linked to account)
     *
     * @param token JWT authentication token for authenticated user
     * @return Result of list of devices on success or error on failure
     */
    override suspend fun getUserDevices(
        token: String
    ): Result<List<AnprDetailsResponseDto>, DataError.Remote> {
        return safeCall<List<AnprDetailsResponseDto>>(
            execute = {
                httpClient.get {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(getDevicesRoute)
                    }
                    bearerAuth(token)
                    contentType(ContentType.Application.Json)
                }
            }
        )
    }

    /**
     * Add device to account
     *
     * @param token JWT authentication token for authenticated user
     * @param request request containing device details
     * @return Result of empty on success or error on failure
     */
    override suspend fun addDeviceToAccount(
        token: String,
        request: AnprDeviceRequestDto
    ): EmptyResult<DataError.Remote> {
        return safeCallEmpty(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(addDeviceToAccountRoute)
                    }
                    bearerAuth(token)
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }

    /**
     * Update selected device details
     *
     * @param token JWT authentication token for authenticated user
     * @param request request containing device details
     * @return Result of empty on success or error on failure
     */
    override suspend fun updateDeviceDetails(
        token: String,
        request: AnprDeviceRequestDto
    ): EmptyResult<DataError.Remote> {
        return safeCallEmpty(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(updateDeviceDetailsRoute)
                    }
                    bearerAuth(token)
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }

    /**
     * Remove device from user's account
     *
     * @param token JWT authentication token for authenticated user
     * @param deviceId device id to be removed
     * @return Result of empty on success or error on failure
     */
    override suspend fun removeDeviceFromAccount(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote> {
        val queryParameters = ParametersBuilder()
        queryParameters.append("deviceId", deviceId)

        return safeCallEmpty(
            execute = {
                httpClient.delete {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(removeDeviceFromAccountRoute)
                        parameters.appendAll(queryParameters.build())
                    }
                    bearerAuth(token)
                }
            }
        )
    }

    /**
     * Register new production device with backend services
     *
     * @param token JWT authentication token for authenticated user
     * @param request request containing device information
     * @return registered device details on success or error on failure
     */
    override suspend fun registerNewProductionDevice(
        token: String,
        request: AnprDeviceRequestDto
    ): Result<AnprDetailsResponseDto, DataError.Remote> {
        return safeCall<AnprDetailsResponseDto>(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(registerProductionDeviceRoute)
                    }
                    bearerAuth(token)
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }

    /**
     * Remove device from user's account
     *
     * @param token JWT authentication token for authenticated user
     * @param deviceId device id to be removed
     * @return Result of empty on success or error on failure
     */
    override suspend fun deleteDeviceFromProduction(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote> {
        val queryParameters = ParametersBuilder()
        queryParameters.append("deviceId", deviceId)

        return safeCallEmpty(
            execute = {
                httpClient.delete {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(deleteDeviceFromProductionRoute)
                        parameters.appendAll(queryParameters.build())
                    }
                    bearerAuth(token)
                }
            }
        )
    }

    /**
     * Get device details file. Requests production device details file (xlsx) from backend services.
     *
     * @param token JWT authentication token for authenticated user
     * @return Result of device details file on success or error on failure
     */
    override suspend fun getDeviceDetailsFile(
        token: String
    ): Result<ByteArray, DataError.Remote> {
        return safeCall<ByteArray>(
            execute = {
                httpClient.get {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(productionDevicesFileRoute)
                    }
                    bearerAuth(token)
                }
            }
        )
    }
}

